import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';

const ROLES = ['owner', 'admin', 'editor', 'operator', 'finance', 'viewer'] as const;
type Role = typeof ROLES[number];

interface TenantUser {
  id: string;
  role: Role;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    fullName: string;
    email: string;
  };
}

interface AddUserForm {
  email: string;
  fullName: string;
  externalAuthId: string;
  role: Role;
}

const EMPTY_FORM: AddUserForm = {
  email: '',
  fullName: '',
  externalAuthId: '',
  role: 'viewer',
};

interface Props {
  tenantId: string;
}

export default function TenantUsersTab({ tenantId }: Props) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<AddUserForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [changingRole, setChangingRole] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<TenantUser[]>(`/tenants/${tenantId}/users`);
      setUsers(data);
    } catch (err) {
      addToast(getApiErrorMessage(err, t('adm_err_users')), 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantId, addToast]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.fullName) {
      addToast('Email and Full Name are required', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/tenants/${tenantId}/users`, form);
      addToast(t('adm_user_added'), 'success');
      setForm(EMPTY_FORM);
      setShowForm(false);
      await loadUsers();
    } catch (err) {
      addToast(getApiErrorMessage(err, t('adm_err_add_user')), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async (tenantUserId: string, newRole: Role) => {
    setChangingRole(tenantUserId);
    try {
      await api.patch(`/tenant-users/${tenantUserId}/role`, { role: newRole });
      setUsers((prev) =>
        prev.map((u) => (u.id === tenantUserId ? { ...u, role: newRole, updatedAt: new Date().toISOString() } : u)),
      );
      addToast(t('adm_role_updated'), 'success');
    } catch (err) {
      addToast(getApiErrorMessage(err, t('adm_err_role')), 'error');
    } finally {
      setChangingRole(null);
    }
  };

  const getRoleBadgeStyle = (role: Role) => {
    const map: Record<Role, { bg: string; color: string }> = {
      owner: { bg: 'var(--surface-2)', color: 'var(--text)' },
      admin: { bg: 'rgba(184,134,11,0.1)', color: '#B8860B' },
      editor: { bg: 'rgba(31,91,143,0.1)', color: '#1F5B8F' },
      operator: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      finance: { bg: 'rgba(107,107,112,0.15)', color: '#4A4A50' },
      viewer: { bg: 'rgba(107,107,112,0.08)', color: '#6B6B70' },
    };
    return map[role] ?? map.viewer;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>
          Users ({users.length})
        </h3>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-colors"
          style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
        >
          <i className="ri-user-add-line mr-1"></i>
          {showForm ? t('common_cancel') : t('adm_add_user')}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={handleAddUser}
          className="mb-5 p-4 rounded-lg"
          style={{ background: '#F5F5F7', border: '1px solid #E5E5E8' }}
        >
          <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#6B6B70' }}>
            Add User to Tenant
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#6B6B70' }}>
                Email <span style={{ color: 'var(--accent)' }}>*</span>
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="user@example.com"
                required
                className="w-full px-3 py-2 rounded-md text-sm outline-none"
                style={{ background: '#fff', border: '1px solid #E5E5E8', color: '#1A1A1A' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#6B6B70' }}>
                Full Name <span style={{ color: 'var(--accent)' }}>*</span>
              </label>
              <input
                type="text"
                value={form.fullName}
                onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                placeholder="John Doe"
                required
                className="w-full px-3 py-2 rounded-md text-sm outline-none"
                style={{ background: '#fff', border: '1px solid #E5E5E8', color: '#1A1A1A' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#6B6B70' }}>
                External Auth ID
              </label>
              <input
                type="text"
                value={form.externalAuthId}
                onChange={(e) => setForm((f) => ({ ...f, externalAuthId: e.target.value }))}
                placeholder="auth0|..."
                className="w-full px-3 py-2 rounded-md text-sm outline-none"
                style={{ background: '#fff', border: '1px solid #E5E5E8', color: '#1A1A1A' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#6B6B70' }}>
                Role <span style={{ color: 'var(--accent)' }}>*</span>
              </label>
              <select
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as Role }))}
                className="w-full px-3 py-2 rounded-md text-sm outline-none cursor-pointer"
                style={{ background: '#fff', border: '1px solid #E5E5E8', color: '#1A1A1A' }}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap transition-opacity disabled:opacity-60"
              style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
            >
              {submitting ? (
                <><i className="ri-loader-4-line animate-spin mr-1"></i>Adding...</>
              ) : (
                t('adm_add_user')
              )}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-10 text-center">
          <i className="ri-loader-4-line animate-spin text-xl" style={{ color: '#6B6B70' }}></i>
          <p className="text-sm mt-2" style={{ color: '#6B6B70' }}>Loading users...</p>
        </div>
      ) : users.length === 0 ? (
        <div className="py-10 text-center">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2"
            style={{ background: '#F5F5F7' }}
          >
            <i className="ri-team-line" style={{ color: '#6B6B70' }}></i>
          </div>
          <p className="text-sm" style={{ color: '#6B6B70' }}>No users assigned to this tenant</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: '#F5F5F7' }}>
                <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>{t('common_user')}</th>
                <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Email</th>
                <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>{t('common_role')}</th>
                <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>{t('common_added2')}</th>
                <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>{t('common_updated')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((tu) => {
                const roleStyle = getRoleBadgeStyle(tu.role);
                const isChanging = changingRole === tu.id;
                return (
                  <tr key={tu.id} style={{ borderBottom: '1px solid #F5F5F7' }}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                          style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
                        >
                          {(tu.user.fullName || '?').slice(0, 1).toUpperCase()}
                        </div>
                        <span className="font-medium" style={{ color: '#1A1A1A' }}>{tu.user.fullName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: '#6B6B70' }}>{tu.user.email}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isChanging ? (
                          <i className="ri-loader-4-line animate-spin text-sm" style={{ color: '#6B6B70' }}></i>
                        ) : null}
                        <select
                          value={tu.role}
                          disabled={isChanging}
                          onChange={(e) => handleRoleChange(tu.id, e.target.value as Role)}
                          className="text-xs font-medium px-2.5 py-1 rounded-full cursor-pointer outline-none disabled:opacity-60"
                          style={{
                            background: roleStyle.bg,
                            color: roleStyle.color,
                            border: `1px solid ${roleStyle.color}30`,
                          }}
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: '#6B6B70' }}>
                      {new Date(tu.createdAt).toLocaleDateString('uk-UA')}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: '#6B6B70' }}>
                      {new Date(tu.updatedAt).toLocaleDateString('uk-UA')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
